import { CommonModule, Location } from "@angular/common";
import { Component, ElementRef, signal, inject, DestroyRef, Renderer2, effect, viewChild } from "@angular/core";
import { takeUntilDestroyed, toSignal, toObservable } from "@angular/core/rxjs-interop";
import { Router, RouterModule, NavigationEnd } from "@angular/router";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { of } from "rxjs";
import { filter, switchMap, delay } from "rxjs/operators";

import { AuthService  } from "@services/auth.service";

import { NavbarService } from "./navbar.service";

@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  standalone: true,
  imports: [CommonModule, RouterModule, NgbModule]
})
export class NavbarComponent {
  private router = inject(Router);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private location = inject(Location);
  private element = inject(ElementRef);
  public navbarService = inject(NavbarService);

  private renderer = inject(Renderer2);
  
  public isCollapsed = signal(false);
  public sidebarVisible = signal(false);
  public mobile_menu_visible = signal(false);

  // Reactive delay for the toggler icon animation
  public isToggled = toSignal(
    toObservable(this.sidebarVisible).pipe(
      switchMap(visible => visible ? of(true).pipe(delay(500)) : of(false)),
      takeUntilDestroyed()
    ),
    { initialValue: false }
  );
  
  navbarToggler = viewChild<ElementRef>("navbarToggler");

  // Use signals directly from the service
  public title = this.navbarService.title;
  public showBackBtn = this.navbarService.showBackBtn;

  constructor() {
    // Synchronize sidebarVisible signal with HTML class
    effect(() => {
      const html = document.getElementsByTagName("html")[0];
      if (this.sidebarVisible()) {
        this.renderer.addClass(html, "nav-open");
      } else {
        this.renderer.removeClass(html, "nav-open");
      }
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.sidebarClose();
    });
  }

  collapse() {
    this.isCollapsed.update(v => !v);
  }

  sidebarOpen() {
    this.sidebarVisible.set(true);
  }

  sidebarClose() {
    this.sidebarVisible.set(false);
    this.mobile_menu_visible.set(false);
  }

  sidebarToggle() {
    if (this.sidebarVisible()) {
      this.sidebarClose();
    } else {
      this.sidebarOpen();
      this.mobile_menu_visible.set(true);
    }
  }

  logout() {
    this.authService.logout();
  }

  goBack(): void {
    this.location.back();
  }
}
